import { useCallback, useRef } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QRCode from 'react-qr-code';
import {
  COPY_KEY,
  HIDE_KEY,
  OTP_SETUP_ENABLE_LABEL,
  OTP_VERIFICATION_LOG_OUT_LABEL,
  SETUP_2FA_STEP_1,
  SETUP_2FA_STEP_2,
  SETUP_2FA_STEP_3,
  SETUP_2FA_SUBTITLE,
  SETUP_2FA_TITLE,
  SHOW_KEY,
  TWO_FA_ORG_MANDATORY_HINT,
} from 'common/constants/auth';
import { Alert, Button, Stack, Text } from 'components/ui';
import type { InputProps } from 'components/ui';
import AuthenticationInput from 'pages/Auth/components/AuthenticationInput';
import AuthFormHeader from 'pages/Auth/components/AuthFormHeader';
import AuthPageStack from 'pages/Auth/components/AuthPageStack';
import {
  ActionButtons,
  OTP_SETUP_QR_CODE_SIZE,
  otpActionButtonSx,
  OtpSetupContent,
  OtpSetupInstruction,
  OtpSetupInstructionBody,
  OtpSetupInstructions,
  OtpSetupInstructionText,
  OtpSetupKeyValue,
  OtpSetupQrCode,
  RootPaper,
  ShowHideOtpKeyButton,
  Subtitle,
} from 'pages/Auth/components/OTP/OTP.styles';
import { OTP_TEST_IDS } from 'pages/Auth/components/OTP/OTP.testIds';
import { isOtpSubmitDisabled } from 'pages/Auth/components/OTP/OTP.utils';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import type { OtpSetupProps } from './OtpSetup.props';

const getFirstErrorMessage = (value?: string | string[]) => {
  if (!value) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};

const OtpSetup = ({
  formState,
  isSubmitting = false,
  otpSecretKey,
  otpUrl,
  showHeader = false,
  showMandatoryHint = false,
  showOtpKey,
  onCancel,
  onCopyOtpKey,
  onInputBlur,
  onInputChange,
  onSubmit,
  onToggleOtpKey,
}: OtpSetupProps) => {
  const alertMessage = getFirstErrorMessage(formState.dbErrors?.otp);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const focusOtpField = useCallback(() => {
    otpInputRef.current?.focus();
  }, []);
  const isSubmitDisabled = isOtpSubmitDisabled(formState, isSubmitting);
  const handleInputKeyDown: NonNullable<InputProps['onKeyDown']> = useCallback(
    event => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      if (!isSubmitDisabled) {
        onSubmit();
      }
    },
    [isSubmitDisabled, onSubmit]
  );

  useAuthInitialFocus(focusOtpField, !isSubmitting);

  return (
    <AuthPageStack>
      {showHeader ? <AuthFormHeader subtitle={SETUP_2FA_SUBTITLE} title={SETUP_2FA_TITLE} /> : null}
      <RootPaper>
        <Stack spacing={4}>
          {!showHeader ? (
            <Stack spacing={2.5}>
              <Subtitle sx={{ color: 'text.primary', fontSize: 18, lineHeight: 1.5, mb: 0 }}>
                {SETUP_2FA_SUBTITLE}
              </Subtitle>
              <Text variant="body2">{SETUP_2FA_TITLE}</Text>
            </Stack>
          ) : null}
          {alertMessage ? (
            <Alert severity="error" visualVariant="inlineError">
              {alertMessage}
            </Alert>
          ) : null}
          {showMandatoryHint ? (
            <Alert data-testid={OTP_TEST_IDS.mandatory2faNotice} severity="info" visualVariant="inline">
              {TWO_FA_ORG_MANDATORY_HINT}
            </Alert>
          ) : null}
          <OtpSetupContent>
            <OtpSetupQrCode>
              <QRCode
                size={OTP_SETUP_QR_CODE_SIZE}
                style={{ height: '100%', width: '100%' }}
                value={otpUrl}
              />
            </OtpSetupQrCode>
            <OtpSetupInstructions>
              <OtpSetupInstruction>
                <OtpSetupInstructionText>{SETUP_2FA_STEP_1}</OtpSetupInstructionText>
              </OtpSetupInstruction>
              <OtpSetupInstruction>
                <OtpSetupInstructionBody>
                  <OtpSetupInstructionText>{SETUP_2FA_STEP_2}</OtpSetupInstructionText>
                  <ShowHideOtpKeyButton
                    data-testid={OTP_TEST_IDS.setupShowKeyButton}
                    onClick={onToggleOtpKey}
                  >
                    {!showOtpKey ? SHOW_KEY : HIDE_KEY}
                  </ShowHideOtpKeyButton>
                  {showOtpKey ? (
                    <OtpSetupKeyValue
                      aria-label={COPY_KEY}
                      data-testid={OTP_TEST_IDS.setupCopyKeyButton}
                      onClick={onCopyOtpKey}
                      type="button"
                    >
                      <span data-testid={OTP_TEST_IDS.setupSecretKeyValue}>{otpSecretKey}</span>
                      <ContentCopyIcon aria-hidden="true" fontSize="small" />
                    </OtpSetupKeyValue>
                  ) : null}
                </OtpSetupInstructionBody>
              </OtpSetupInstruction>
              <OtpSetupInstruction>
                <OtpSetupInstructionText>{SETUP_2FA_STEP_3}</OtpSetupInstructionText>
              </OtpSetupInstruction>
            </OtpSetupInstructions>
          </OtpSetupContent>
          <AuthenticationInput
            disabled={isSubmitting}
            formState={formState}
            handleBlur={onInputBlur}
            handleChange={onInputChange}
            inputRef={otpInputRef}
            onKeyDown={handleInputKeyDown}
          />
        </Stack>
        <ActionButtons>
          <Button disabled={isSubmitting} id="cancel-btn" onClick={onCancel} variant="text" visualVariant="primaryGhost">
            {OTP_VERIFICATION_LOG_OUT_LABEL}
          </Button>
          <Button
            data-testid={OTP_TEST_IDS.setupEnableButton}
            disabled={isSubmitDisabled}
            loading={isSubmitting}
            onClick={onSubmit}
            variant="contained"
            visualVariant="positive"
            sizeVariant="compact"
            sx={otpActionButtonSx}
          >
            {OTP_SETUP_ENABLE_LABEL}
          </Button>
        </ActionButtons>
      </RootPaper>
    </AuthPageStack>
  );
};

export default OtpSetup;
